import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { apiRequest, ApiError } from "../api/client";

export interface User {
  id: number;
  email: string;
  isAdmin?: boolean;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  loading: boolean;
  loginWithCredentials: (email: string, password: string) => Promise<void>;
  registerWithCredentials: (email: string, password: string) => Promise<void>;
  loginWithToken: (token: string) => Promise<void>;
  refreshUser: () => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const applySession = useCallback((newToken: string | null, nextUser?: User | null) => {
    if (newToken) {
      setToken(newToken);
      localStorage.setItem("token", newToken);
    } else {
      setToken(null);
      localStorage.removeItem("token");
    }
    if (nextUser !== undefined) {
      setUser(nextUser);
    }
  }, []);

  const fetchCurrentUser = useCallback(
    async (authToken?: string) => {
      const activeToken = authToken ?? token;
      if (!activeToken) return;
      try {
        const current = await apiRequest<User>("/auth/me", {
          token: activeToken,
        });
        applySession(activeToken, current);
      } catch (error) {
        const err = error as ApiError;
        if (err.status === 401) {
          applySession(null, null);
        }
        throw err;
      }
    },
    [applySession, token]
  );

  const loginWithCredentials = useCallback(
    async (email: string, password: string) => {
      const result = await apiRequest<{ token: string; user: User }>("/auth/login", {
        method: "POST",
        body: { email, password },
      });
      applySession(result.token, result.user);
    },
    [applySession]
  );

  const registerWithCredentials = useCallback(
    async (email: string, password: string) => {
      const result = await apiRequest<{ token: string; user: User }>("/auth/register", {
        method: "POST",
        body: { email, password },
      });
      applySession(result.token, result.user);
    },
    [applySession]
  );

  const loginWithToken = useCallback(
    async (incomingToken: string) => {
      applySession(incomingToken);
      await fetchCurrentUser(incomingToken);
    },
    [applySession, fetchCurrentUser]
  );

  const refreshUser = useCallback(async () => {
    await fetchCurrentUser();
  }, [fetchCurrentUser]);

  const logout = useCallback(() => {
    applySession(null, null);
  }, [applySession]);

  React.useEffect(() => {
    const stored = localStorage.getItem("token");
    if (!stored) {
      setLoading(false);
      return;
    }

    loginWithToken(stored)
      .catch(() => {
        applySession(null, null);
      })
      .finally(() => setLoading(false));
  }, [applySession, loginWithToken]);

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      loginWithCredentials,
      registerWithCredentials,
      loginWithToken,
      refreshUser,
      logout,
    }),
    [user, token, loading, loginWithCredentials, registerWithCredentials, loginWithToken, refreshUser, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth debe utilizarse dentro de un AuthProvider");
  }
  return context;
}
